import React, { useEffect, useState } from 'react';
import { APP_STATE_SQL, APP_STATE_TABLE, MODEL_TABLES, SCHEMA_SQL, TIPOS_SUCURSAL_SEED_SQL, SUPABASE_URL, getConnectionStatus, getModelStatus, type ConnectionStatus } from '../supabase';
import { glassLight, primaryButton, ghostButton, T } from '../ui';
type Status = ConnectionStatus | 'checking';
const STATUS_META: Record<Status, {
  label: string;
  color: string;
  soft: string;
}> = {
  checking: {
    label: 'Comprobando…',
    color: T.textMuted,
    soft: 'rgba(255,255,255,0.06)'
  },
  connected: {
    label: 'Conectado',
    color: T.ok,
    soft: T.okSoft
  },
  'no-table': {
    label: 'Falta crear',
    color: T.warn,
    soft: T.warnSoft
  },
  offline: {
    label: 'Sin conexión',
    color: T.danger,
    soft: T.dangerSoft
  }
};
function StatusPill({
  status,
  label
}: {
  status: Status;
  label?: string;
}) {
  const meta = STATUS_META[status];
  return <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    background: meta.soft,
    border: `1px solid ${meta.color}44`,
    color: meta.color,
    borderRadius: 99,
    padding: '5px 11px',
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: 'nowrap'
  }} data-id="element-315" data-mp="2aww85lqjqj9r">
      
      <span style={{
      width: 7,
      height: 7,
      borderRadius: '50%',
      background: meta.color,
      boxShadow: `0 0 7px ${meta.color}`
    }} data-id="element-316" data-mp="3egjlv8gcvmza" />
      
      {label ?? meta.label}
    </span>;
}
function SqlBlock({
  sql
}: {
  sql: string;
}) {
  return <pre style={{
    margin: 0,
    background: 'rgba(0,0,0,0.35)',
    border: `1px solid ${T.border}`,
    borderRadius: 12,
    padding: 16,
    fontSize: 12,
    lineHeight: 1.55,
    color: 'rgba(255,255,255,0.78)',
    fontFamily: "'SFMono-Regular', ui-monospace, Menlo, Consolas, monospace",
    overflowX: 'auto',
    whiteSpace: 'pre'
  }} data-id="element-317" data-mp="2qpxs8m73m58t">
      
      {sql}
    </pre>;
}
export function ConexionSupabase() {
  const [kvStatus, setKvStatus] = useState<Status>('checking');
  const [model, setModel] = useState<Record<string, Status>>(() => Object.fromEntries(MODEL_TABLES.map(t => [t.name, 'checking'])));
  const [checking, setChecking] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [showSchema, setShowSchema] = useState(false);
  const [showSeed, setShowSeed] = useState(false);
  const check = async () => {
    setChecking(true);
    setKvStatus('checking');
    setModel(Object.fromEntries(MODEL_TABLES.map(t => [t.name, 'checking'])));
    const [kv, m] = await Promise.all([getConnectionStatus(), getModelStatus()]);
    setKvStatus(kv);
    setModel(m);
    setChecking(false);
  };
  useEffect(() => {
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const copy = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(c => c === id ? null : c), 2000);
    } catch {

      /* clipboard no disponible */}
  };
  const offline = kvStatus === 'offline';
  const pendientes = MODEL_TABLES.filter(t => model[t.name] === 'no-table').length;
  const creadas = MODEL_TABLES.filter(t => model[t.name] === 'connected').length;
  return <div style={{
    ...glassLight,
    padding: 22
  }} data-id="element-318" data-mp="ljchprvuvaap">
      
      <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
      marginBottom: 16
    }} data-id="element-319" data-mp="lgzq0n0aplkg">
        
        <div style={{
        fontSize: 11,
        fontWeight: 700,
        color: T.textMuted,
        letterSpacing: '0.1em',
        textTransform: 'uppercase'
      }} data-id="element-320" data-mp="1e5f2p2vyfrsm">
          
          Conexión y base de datos
        </div>
        <button onClick={check} disabled={checking} className="getr-btn-ghost" style={{
        ...ghostButton,
        opacity: checking ? 0.6 : 1
      }} data-id="element-321" data-mp="19859rd9cwy01">
          
          {checking ? 'Verificando…' : 'Volver a verificar'}
        </button>
      </div>

      {/* Estado general + proyecto */}
      <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
      paddingBottom: 14,
      borderBottom: `1px solid ${T.border}`,
      marginBottom: 16
    }} data-id="element-322" data-mp="e04np8wgk5pn">
        
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10
      }} data-id="element-323" data-mp="48gyzfrad2rv">
          
          <StatusPill status={offline ? 'offline' : 'connected'} label={offline ? 'Sin conexión' : 'Supabase activo'} data-id="element-324" data-mp="3vf1lqnab5oj3" />
          
          <span style={{
          fontSize: 12,
          color: T.textMuted,
          wordBreak: 'break-all'
        }} data-id="element-325" data-mp="rzmaxju3vaww">
            
            {SUPABASE_URL}
          </span>
        </div>
        {!offline && <span style={{
        fontSize: 12,
        color: T.textSecondary,
        fontWeight: 600
      }} data-id="element-326" data-mp="i2rfxax1jsnd">
          
            {creadas}/{MODEL_TABLES.length} tablas creadas
          </span>}
      </div>

      {offline ? <div style={{
      fontSize: 13,
      color: T.textSecondary,
      lineHeight: 1.5
    }} data-id="element-327" data-mp="32rff165sa3s0">
        
          No se pudo contactar a Supabase. La app sigue funcionando con datos
          locales y se sincronizará cuando vuelva la conexión.
        </div> : <>
          {/* Tablas del modelo */}
          <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginBottom: 16
      }} data-id="element-328" data-mp="1s6rf2c8merg3">
          
            {MODEL_TABLES.map(t => <div key={t.name} style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '10px 14px',
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: 12
        }} data-id="element-329" data-mp="21yxbby5wnv13">
            
                <div style={{
            minWidth: 0
          }} data-id="element-330" data-mp="2fxesfd1b29a1">
              
                  <div style={{
              fontSize: 13,
              fontWeight: 700,
              color: T.text
            }} data-id="element-331" data-mp="glughvksnfjl">
                
                    {t.label}
                  </div>
                  <code style={{
              fontSize: 11,
              color: T.textMuted,
              fontFamily: "'SFMono-Regular', ui-monospace, Menlo, monospace"
            }} data-id="element-332" data-mp="djzgara6ii9e">
                
                    {t.name}
                  </code>
                </div>
                <StatusPill status={model[t.name] ?? 'checking'} data-id="element-333" data-mp="ii17jzhrdnvy" />
              </div>)}
          </div>

          {/* Acción principal: crear el esquema */}
          <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        marginBottom: showSchema ? 12 : 0
      }} data-id="element-334" data-mp="wfpiofm0xgnt">
          
            <div style={{
          fontSize: 13,
          color: T.textSecondary,
          lineHeight: 1.5
        }} data-id="element-335" data-mp="11922mcfrcza8">
            
              {pendientes === 0 ? 'Todas las tablas del modelo están creadas. ✓' : `Faltan ${pendientes} tabla${pendientes > 1 ? 's' : ''}. Ejecuta el SQL del esquema en el editor SQL de Supabase.`}
            </div>
            <div style={{
          display: 'flex',
          gap: 8
        }} data-id="element-336" data-mp="1utjgv47qfwtr">
            
              <button onClick={() => setShowSchema(s => !s)} className="getr-btn-ghost" style={ghostButton} data-id="element-337" data-mp="1yttp6nizyzbs">
              
                {showSchema ? 'Ocultar SQL' : 'Ver SQL del esquema'}
              </button>
              <button onClick={() => copy('schema', SCHEMA_SQL)} style={{
            ...primaryButton,
            padding: '8px 16px',
            fontSize: 12
          }} data-id="element-338" data-mp="1pvrq4ditqgr5">
              
                {copied === 'schema' ? 'Copiado ✓' : 'Copiar esquema'}
              </button>
            </div>
          </div>

          {showSchema && <SqlBlock sql={SCHEMA_SQL} data-id="element-339" data-mp="172nu4li4vrtd" />}

          {/* Datos semilla: catálogo de tipos de sucursal (gasolineras, etc.) */}
          <div style={{
        marginTop: 18,
        paddingTop: 14,
        borderTop: `1px solid ${T.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        marginBottom: showSeed ? 12 : 0
      }} data-id="element-340" data-mp="s6y5sgph72lh">
          
            <div style={{
          fontSize: 13,
          color: T.textSecondary,
          lineHeight: 1.5
        }} data-id="element-341" data-mp="18lxjm970b1rm">
            
              Carga inicial del catálogo "tipos_sucursal" (gasolineras, estaciones de gas, etc.).
            </div>
            <div style={{
          display: 'flex',
          gap: 8
        }} data-id="element-342" data-mp="tnwsrtkzjg4c">
            
              <button onClick={() => setShowSeed(s => !s)} className="getr-btn-ghost" style={ghostButton} data-id="element-343" data-mp="oxorfz5gqvs4">
              
                {showSeed ? 'Ocultar SQL' : 'Ver SQL de datos'}
              </button>
              <button onClick={() => copy('seed', TIPOS_SUCURSAL_SEED_SQL)} style={{
            ...primaryButton,
            padding: '8px 16px',
            fontSize: 12
          }} data-id="element-344" data-mp="fxa4ywgrjan1">
              
                {copied === 'seed' ? 'Copiado ✓' : 'Copiar inserts'}
              </button>
            </div>
          </div>

          {showSeed && <SqlBlock sql={TIPOS_SUCURSAL_SEED_SQL} data-id="element-345" data-mp="3rzi6b6xymfss" />}

          {/* Tabla de respaldo key-value */}
          <div style={{
        marginTop: 18,
        paddingTop: 14,
        borderTop: `1px solid ${T.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap'
      }} data-id="element-346" data-mp="s5n6mdinsa8">
          
            <div data-id="element-347" data-mp="39abn7it97m6g">
              <div style={{
            fontSize: 12,
            fontWeight: 700,
            color: T.text
          }} data-id="element-348" data-mp="39av6b2un4gm2">
              
                Respaldo automático ({APP_STATE_TABLE})
              </div>
              <div style={{
            fontSize: 12,
            color: T.textMuted,
            marginTop: 2
          }} data-id="element-349" data-mp="3j6ez3gdwqz6l">
              
                Guarda una copia completa del estado mientras migramos a las
                tablas del modelo.
              </div>
            </div>
            <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }} data-id="element-350" data-mp="2vgknf25brgnx">
            
              <StatusPill status={kvStatus} data-id="element-351" data-mp="3rlknmdiooaho" />
              {kvStatus === 'no-table' && <button onClick={() => copy('kv', APP_STATE_SQL)} className="getr-btn-ghost" style={ghostButton} data-id="element-352" data-mp="bcaugtfdkgh">
              
                  {copied === 'kv' ? 'Copiado ✓' : 'Copiar SQL'}
                </button>}
            </div>
          </div>
        </>}
    </div>;
}